import { useTranslation } from "react-i18next";
import { type FareBreakdown, INITIAL_PASSENGERS } from "src/Modules/Flights/constants";
import { formatPassengers } from "src/Modules/Flights/constants/JourneyDetails";
import { useJourneyStaticDetails } from "../../context";
import { explode, formatNumber } from "src/utility";
import SkeletonBox from "src/components/SkeletonBox/SkeletonBox";
import BreakdownCard from "../BreakdownCard/BreakdownCard";

export default function PassengerBreakdown() {
  const { t } = useTranslation();
  const { journeyDetails, searchObject } = useJourneyStaticDetails();
  const { data, isFetching } = explode(journeyDetails, {});
  const fareBreakdown = data?.fareBreakdown;
  const currency = data?.fare?.currency ?? "";

  if (!isFetching && !fareBreakdown) return null;

  const { adultCount, childCount, infantCount } = searchObject.passengers ?? INITIAL_PASSENGERS;
  const skeletonCounts = [adultCount, childCount, infantCount].filter((c) => c > 0);

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="text-base text-font-default font-semibold">{t("Flights.details.summary.passengerBreakdown")}</div>
      {isFetching ? (
        <div className="flex flex-col gap-2">
          {skeletonCounts.map((_, i) => (
            <SkeletonBox key={i} className="h-12 rounded-md" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-4 w-full">
          {fareBreakdown?.map((fare: FareBreakdown) => (
            <PassengerFareDetail key={fare.passengerType} fare={fare} currency={currency} />
          ))}
        </div>
      )}
    </div>
  );
}

function PassengerFareDetail({ fare, currency }: { fare: FareBreakdown; currency: string }) {
  const { t } = useTranslation();
  const passengerData = formatPassengers(fare.passengerCount).find((e) => e.key === fare.passengerType)!;
  return (
    <BreakdownCard
      header={{
        icon: passengerData?.icon,
        label: `${passengerData?.label} (x${fare.passengerCount})`,
        value: `${formatNumber(fare.baseFare + fare.tax)} ${currency}`,
      }}
      rows={[
        {
          label: t("Flights.details.summary.baseFareBreakdown", { count: fare.passengerCount, amount: formatNumber(fare.baseFarePerPax) }),
          value: `${formatNumber(fare.baseFare)} ${currency}`,
        },
        {
          label: t("Flights.details.summary.taxesBreakdown", { count: fare.passengerCount, amount: formatNumber(fare.taxPerPax) }),
          value: `${formatNumber(fare.tax)} ${currency}`,
        },
      ]}
    />
  );
}
